import { cardSx, hubPageBg, HubSwitch } from './accountHubStyles';
import { useAsUserParam, useProxyTargetMember } from './useAsUserParam';
import AccountContactsEditor from './AccountContactsEditor';
import { FC, useCallback, useState } from 'react';
import { useParams } from 'react-router';
import {
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import InfoIconButton from './InfoIconButton';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import UlcmContactService from 'src/services/UlcmContact.service';
import type {
  UlcmContactsPayload,
  CommunicationPreference,
} from 'src/services/UlcmContact.service';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from 'src/redux/rootReducerType';
import { updateUser } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';

// ---------------------------------------------------------------------------
// Shared sub-styles
// ---------------------------------------------------------------------------

const sectionLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  mb: 1,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ContactInfoPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const proxyTarget = useProxyTargetMember();
  const isProxy = proxyTarget !== undefined;
  const ownerName = proxyTarget?.first_name?.trim() || undefined;
  const targetUserId = userId ? Number(userId) : null;
  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${
    userId ?? ''
  }`;

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const dispatch = useDispatch();

  // Mirror of the contacts snapshot owned by AccountContactsEditor so the
  // Preferences card can read communication_preference.
  const [contacts, setContacts] = useState<UlcmContactsPayload | null>(null);

  // ── Comm preference ──────────────────────────────────────────────────────
  // Optimistic update with rollback: apply the change locally for snappy UX,
  // then revert if the API rejects. Prevents the UI from silently drifting
  // from the server when the PATCH fails.
  const handleCommPrefChange = useCallback(
    async (value: string): Promise<void> => {
      const previous = contacts?.communication_preference;
      setContacts((prev) =>
        prev
          ? {
              ...prev,
              communication_preference: value as CommunicationPreference,
            }
          : prev
      );
      try {
        await UlcmContactService.patchContactPreferences({
          communication_preference: value as CommunicationPreference,
        });
      } catch {
        setContacts((prev) =>
          prev
            ? {
                ...prev,
                communication_preference: previous,
              }
            : prev
        );
      }
    },
    [contacts?.communication_preference]
  );

  // ── SMS preference toggles ────────────────────────────────────────────────
  const handlePrefToggle = useCallback(
    async (
      field:
        | 'sms_opt_in_customer_care'
        | 'sms_opt_in_account_notifications'
        | 'sms_opt_in_otp_2fa'
        | 'receive_renter_notifications'
        | 'receive_renter_notifications_email',
      value: boolean
    ): Promise<void> => {
      const previous = Boolean(user?.[field]);
      dispatch(updateUser({ [field]: value }));
      try {
        await UlcmContactService.patchContactPreferences({ [field]: value });
      } catch {
        dispatch(updateUser({ [field]: previous }));
      }
    },
    [dispatch, user]
  );

  const commPref = contacts?.communication_preference ?? '';

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Contact Information"
        icon={<EmailIcon />}
        backRoute={backRoute}
        subtitle="Manage your email, phone, and address"
        hideBackOnDesktop
      />
      <ProxyBanner />

      <Stack gap={2.5} sx={{ p: 3 }}>
        <AccountContactsEditor
          asUser={asUser}
          targetUserId={targetUserId}
          ownerName={ownerName}
          onContactsChange={setContacts}
        />

        {/* ── Preferences card ──
            Preferences are per-account and aren't loaded for the proxied user,
            so they're read-only while managing someone else (the underlying
            PATCH would otherwise write to the caller's own account). */}
        {isProxy ? (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
              Preferences
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Contact and notification preferences can only be changed by the
              account owner.
            </Typography>
          </Box>
        ) : (
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 2 }}>
              Preferences
            </Typography>

            {/* Communication preference selector */}
            <Stack gap={0.75} sx={{ mb: 2 }}>
              <Typography sx={sectionLabelSx}>
                Preferred Contact Method
              </Typography>
              <Select
                value={commPref}
                onChange={(e) => void handleCommPrefChange(e.target.value)}
                size="small"
                displayEmpty
                sx={{ fontSize: 14 }}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="mobile_phone">SMS / Phone</MenuItem>
              </Select>
            </Stack>

            <Typography sx={sectionLabelSx}>SMS Preferences</Typography>
            <Stack gap={1} sx={{ mb: 2 }}>
              {[
                {
                  field: 'sms_opt_in_customer_care' as const,
                  label: 'Customer Care Communications',
                  info: "Direct text messages from your property's compliance team, such as requests for documents or replies to your questions.",
                },
                {
                  field: 'sms_opt_in_account_notifications' as const,
                  label: 'Account Notifications',
                  info: 'Automated text updates about your application or re-certification, such as upcoming deadlines and status changes.',
                },
                {
                  field: 'sms_opt_in_otp_2fa' as const,
                  label: 'Login Codes (OTP)',
                },
              ].map(({ field, label, info }) => (
                <Stack
                  key={field}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: 'action.hover',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 1.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                    {info && <InfoIconButton label={label} info={info} />}
                  </Stack>
                  <HubSwitch
                    checked={Boolean(user?.[field as keyof typeof user])}
                    onChange={(_, checked) =>
                      void handlePrefToggle(field, checked)
                    }
                  />
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Typography sx={sectionLabelSx}>
              Notification Preferences
            </Typography>
            <Stack gap={1}>
              {[
                {
                  label: 'In-App Notifications',
                  field: 'receive_renter_notifications' as const,
                  checked: Boolean(user?.receive_renter_notifications),
                },
                {
                  label: 'Email/SMS Notifications',
                  field: 'receive_renter_notifications_email' as const,
                  checked: Boolean(user?.receive_renter_notifications_email),
                },
              ].map(({ label, field, checked }) => (
                <Stack
                  key={label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: 'action.hover',
                    borderRadius: '8px',
                    px: 1.5,
                    py: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                  <HubSwitch
                    checked={checked}
                    onChange={(_, value) => void handlePrefToggle(field, value)}
                  />
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ContactInfoPage;
